import { loadWithRetry, match } from '@frontend/duck-tape';
import type { ReactNode } from '@frontend/react';
import { createContext, useCallback, useContext, useEffect, useState } from '@frontend/react';
import { YStack } from '@frontend/web-react';
import { addSentryBreadcrumb, captureSentryMessage } from '@frontend/web-utils';
import { Env } from '@frontend/web/utils';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { ComponentType } from 'react';
import { stripeBaseElementsOptions } from '../Payment.constants';
import { StripeLoading } from './components/StripeLoading';
import { StripeUnavailable } from './components/StripeUnavailable';

export type StripeElementKind = 'cardCvc' | 'cardExpiry' | 'cardNumber';

const DEFAULT_ELEMENTS_TIMEOUT_MS = 10_000;
const STRIPE_LOAD_TIMEOUT_MS = 10_000;
const STRIPE_RETRY_DELAY_MS = 1_500;
const STRIPE_TOTAL_ATTEMPTS = 3;
const DEFAULT_EXPECTED_ELEMENTS: readonly StripeElementKind[] = ['cardNumber', 'cardExpiry', 'cardCvc'];

type StripeLoadResult = { status: 'failure' } | { status: 'success'; value: Stripe };

const StripeRegisterReadyContext = createContext<MaybeUndefined<(element: StripeElementKind) => void>>(undefined);

export const useStripeRegisterReady = (): MaybeUndefined<(element: StripeElementKind) => void> =>
  useContext(StripeRegisterReadyContext);

const isStripeScriptInjected = (): boolean =>
  typeof document !== 'undefined' && document.querySelector('script[src*="js.stripe.com"]') !== null;

type StripeProviderProps = {
  children: ReactNode;
  elementsTimeoutMs?: number;
  expectedElements?: readonly StripeElementKind[];
  loadingFallback?: ReactNode;
  options?: StripeElementsOptions;
  publishableKey?: string;
  reloadFallback?: ReactNode;
  retryFallback?: (onRetry: () => void) => ReactNode;
};

export const StripeProvider = ({
  children,
  elementsTimeoutMs = DEFAULT_ELEMENTS_TIMEOUT_MS,
  expectedElements = DEFAULT_EXPECTED_ELEMENTS,
  loadingFallback,
  options,
  publishableKey = Env.VITE_STRIPE_PUBLIC_KEY,
  reloadFallback,
  retryFallback,
}: StripeProviderProps) => {
  const [loadResult, setLoadResult] = useState<MaybeUndefined<StripeLoadResult>>(undefined);
  const [readyElements, setReadyElements] = useState<Partial<Record<StripeElementKind, true>>>({});
  const [hasElementsTimedOut, setHasElementsTimedOut] = useState(false);
  const [loadAttemptId, setLoadAttemptId] = useState(0);

  const registerReady = useCallback((element: StripeElementKind) => {
    setReadyElements((current) => (current[element] ? current : { ...current, [element]: true }));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    loadWithRetry({
      load: async () => {
        const stripe = await loadStripe(publishableKey);
        if (!stripe) throw new Error('Stripe loader returned null');
        return stripe;
      },
      onFailedAttempt: ({ attemptNumber, error }) => {
        addSentryBreadcrumb({
          category: 'third_party.load',
          level: 'warning',
          message: `stripe load attempt ${attemptNumber} failed (${error.message})`,
        });
      },
      onSuccess: ({ attemptNumber }) => {
        addSentryBreadcrumb({
          category: 'third_party.load',
          level: 'info',
          message: `stripe loaded on attempt ${attemptNumber}`,
        });
      },
      retryDelayMs: STRIPE_RETRY_DELAY_MS,
      signal: controller.signal,
      timeoutMs: STRIPE_LOAD_TIMEOUT_MS,
      totalAttempts: STRIPE_TOTAL_ATTEMPTS,
    })
      .then((value) => {
        if (controller.signal.aborted) return;
        setLoadResult({ status: 'success', value });
      })
      .catch((error: Error) => {
        if (controller.signal.aborted) return;
        captureSentryMessage(
          `Failed to load stripe after ${STRIPE_TOTAL_ATTEMPTS} attempts (${error.message})`,
          'error',
        );
        setLoadResult({ status: 'failure' });
      });

    return () => controller.abort();
  }, [publishableKey, loadAttemptId]);

  const onSdkRetry = useCallback(() => {
    setLoadResult(undefined);
    setReadyElements({});
    setHasElementsTimedOut(false);
    setLoadAttemptId((id) => id + 1);
  }, []);

  const onElementsTimeoutRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const sdkLoaded = loadResult?.status === 'success';
  const allElementsReady = expectedElements.every((element) => readyElements[element]);

  useEffect(() => {
    if (!sdkLoaded || allElementsReady || hasElementsTimedOut) return;
    const timeoutHandle = setTimeout(() => setHasElementsTimedOut(true), elementsTimeoutMs);
    return () => clearTimeout(timeoutHandle);
  }, [sdkLoaded, allElementsReady, hasElementsTimedOut, elementsTimeoutMs]);

  const stripeValue = loadResult?.status === 'success' ? loadResult.value : null;
  const elementsKind = match({ allElementsReady, hasElementsTimedOut, loadResult })
    .with({ loadResult: undefined }, () => 'sdk-loading' as const)
    .with({ loadResult: { status: 'failure' } }, () => 'sdk-failed' as const)
    .with({ allElementsReady: false, hasElementsTimedOut: true }, () => 'elements-timed-out' as const)
    .with({ allElementsReady: false }, () => 'elements-pending' as const)
    .otherwise(() => 'ready' as const);

  return match(elementsKind)
    .with('sdk-loading', () => loadingFallback ?? <StripeLoading />)
    .with('sdk-failed', () =>
      isStripeScriptInjected()
        ? reloadFallback ?? <StripeUnavailable onAction={() => window.location.reload()} variant="reload" />
        : retryFallback?.(onSdkRetry) ?? <StripeUnavailable onAction={onSdkRetry} />,
    )
    .with('elements-timed-out', () => <StripeUnavailable onAction={onElementsTimeoutRetry} variant="reload" />)
    .with('elements-pending', 'ready', (kind) => (
      <StripeRegisterReadyContext.Provider value={registerReady}>
        <YStack className="relative">
          <YStack style={{ visibility: kind === 'ready' ? 'visible' : 'hidden' }}>
            <Elements options={options ?? stripeBaseElementsOptions} stripe={Promise.resolve(stripeValue)}>
              {children}
            </Elements>
          </YStack>
          {kind === 'elements-pending' ? (
            <YStack className="absolute inset-none items-center justify-center">
              {loadingFallback ?? <StripeLoading />}
            </YStack>
          ) : null}
        </YStack>
      </StripeRegisterReadyContext.Provider>
    ))
    .exhaustive();
};

export const withStripe = <TProps extends GenericRecord>(WrappedComponent: ComponentType) => {
  const StripeHOC = (props: TProps) => (
    <StripeProvider>
      <WrappedComponent {...props} />
    </StripeProvider>
  );
  return StripeHOC;
};
